import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useFormContext } from "react-hook-form";
import { Link, useHistory } from "react-router-dom";
import { UserApi } from "../../api/UserApi";
import { useAuth } from "../../App";
import { ISearchResult, ISearchResultType } from "../../models/IMultiSearch";
import { ISearchableUser } from "../../models/IUser";
import "./SearchUser.scss";

interface IProps {
    label: string;
    name: string;
    labelCol?: number;
    inputCol?: number;
    placeholder?: string;
    onChange: (val: string) => void;
    required?: boolean;
    errorMessage?: string;
}

export const SearchUser: React.FC<IProps> = ({ label, name, labelCol, inputCol, placeholder, onChange, required, errorMessage }) => {
    const { register, setValue, errors, unregister } = useFormContext();
    const [inputValue, setInputValue] = React.useState("");
    const [results, setResults] = React.useState<ISearchResult[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const auth = useAuth();
    var userApi = new UserApi(auth);
    const [searchableUsers, setSearchableUsers] = React.useState<ISearchableUser[]>();
    const [selectedIndex, setSelectedIndex] = React.useState<number>(-1);
    const [selectedResultType, setSelectedResultType] = React.useState<ISearchResultType>();
    const history = useHistory();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const resultLayerRef = React.useRef<HTMLUListElement>(null);

    React.useEffect(() => {
        document.addEventListener("click", handleDocumentClick);
        return () => {
            document.removeEventListener("click", handleDocumentClick);
        };
    }, []);

    React.useEffect(() => {
        register({ name: name }, { required: required });

        return () => {
            unregister(name);
        }

    }, [searchableUsers, required]);

    const getSearchableUsers = () => {
        setIsLoading(true);
        userApi.getSearchableUsers(
            (result) => {
                setSearchableUsers(result);
                setIsLoading(false);
            },
            undefined,
            undefined
        );
    };

    const mapIndexes = (seachResult: ISearchResult[]) => {
        seachResult.map((key, index) => { key.index = index });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const prevSelectedIndex = -1;
        setInputValue(inputValue);
        setSelectedIndex(-1);

        if (inputValue.length >= 2) {
            const matchingUsers = searchableUsers!.filter(
                (cp) =>
                    cp.hsaId?.toLowerCase().includes(inputValue.toLowerCase()) ||
                    cp.name.toLowerCase().includes(inputValue.toLowerCase())
            );

            const newResults: ISearchResult[] = [...(matchingUsers || [])
                .slice(0, 10)
                .map((cp, i) => ({
                    id: cp.userId,
                    name: `${cp.hsaId} - ${cp.name}`,
                    type: ISearchResultType.User,
                    index: 0
                }))
            ];

            mapIndexes(newResults);
            setResults(newResults);
        } else {
            setResults([]);
        }

        if (prevSelectedIndex >= 0 && inputValue.length > 0) {
            setSelectedIndex(prevSelectedIndex);
        }
    };
    const handleSelectResult = (result: ISearchResult) => {
        setInputValue("");
        setResults([]);
        setSelectedIndex(-1)
        setSelectedResultType(undefined);
        setSearchableUsers(undefined);

        if (result.type === ISearchResultType.User) {
            onChange(result.id.toString());
        }

        inputRef.current?.blur();
    };

    const handleFocus = () => {
        getSearchableUsers();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const { key } = e;
        switch (key) {
            case "ArrowDown":
                e.preventDefault();
                const nextResultIndex = selectedIndex + 1;
                if (nextResultIndex < results.length) {
                    setSelectedIndex(nextResultIndex);
                    setSelectedResultType(results[nextResultIndex].type);
                    const selectedResultEl = resultLayerRef.current?.querySelector(`[data-index="${nextResultIndex}"]`);
                    if (selectedResultEl) {
                        selectedResultEl.scrollIntoView({ block: "nearest" });
                    }
                }
                break;
            case "ArrowUp":
                e.preventDefault();
                const prevResultIndex = selectedIndex - 1;
                if (prevResultIndex >= 0) {
                    setSelectedIndex(prevResultIndex);
                    setSelectedResultType(results[prevResultIndex].type);
                    const selectedResultEl = resultLayerRef.current?.querySelector(`[data-index="${prevResultIndex}"]`);
                    if (selectedResultEl) {
                        if (selectedIndex === 1) {
                            selectedResultEl.scrollIntoView({ block: "end" });
                        } else {
                            selectedResultEl.scrollIntoView({ block: "nearest" });
                        }

                    }
                }
                break;
            case "Enter":
                e.preventDefault();
                const selectedResult = results.find(
                    (result) =>
                        result.type === selectedResultType && result.index === selectedIndex
                );
                if (selectedResult) {
                    handleSelectResult(selectedResult);
                }
                break;
            default:
                break;
        }
    };

    const handleClearInput = () => {
        setInputValue("");
        setSelectedIndex(-1);
        setResults([]);
    };

    const handleResultLayerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    const handleDocumentClick = () => {
        setInputValue("");
        setSelectedIndex(-1);
        setResults([]);
    }

    const getMappedListItems = (resultType: ISearchResultType) => {
        return <>
            {
                results
                    .filter((result) => result.type === resultType)
                    .map((result) => (
                        <li key={result.id} className={`result-item ${result.type === selectedResultType && result.index === selectedIndex ? "selected" : ""}`} data-index={result.index}>
                            <Link to={`#`} onClick={() => handleSelectResult(result)}>{result.name}</Link>
                        </li>
                    ))
            }
        </>
    }

    const showErrorMessage = () => {
        if (errorMessage) {
            return errorMessage;
        } else {
            return "V�lj en anv�ndare";
        }
    }

    return (
        <div className="form-link form-group row">
            <label className={`col-${labelCol ?? 4} col-form-label`}>{label}:{required ? "*" : ""}</label>
            <div className={`col-${inputCol ?? 8} position-relative multi-search`}>
                <div className="input-group">
                    <label className="visually-hidden" htmlFor="multi-search">S�k anv�ndare</label>
                    <input
                        id={name}
                        autoComplete="off"
                        type="text"
                        onFocus={handleFocus}
                        className="form-control"
                        placeholder={isLoading ? "Laddar..." : placeholder}
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        ref={inputRef} />
                    {inputValue && (
                        <div className="clear-icon" onClick={handleClearInput}>
                            <FontAwesomeIcon icon={faTimes} />
                        </div>
                    )}
                </div>
                {inputValue && results.length > 0 && (
                    <div className="result-layer" onClick={handleResultLayerClick}>
                        <ul className="list-unstyled m-0" ref={resultLayerRef}>
                            {
                                getMappedListItems(ISearchResultType.User)
                            }
                        </ul>
                    </div>
                )}
                <span className="text-danger">{errors ? [name] && (errors[name] as any)?.type === "required" && showErrorMessage() : ""}</span>
            </div>
        </div>
    );
};